import React from "react"

import { Card, CardContent } from "@/components/ui/card"
import { Form } from "@/components/ui/form"
import { TrialBlock } from "@/types/trialBlock"

import AdditionalTrialSection from "./review-form/AdditionalTrialSection"
import AttendedToggleSection from "./review-form/AttendedToggleSection"
import CommentsSection from "./review-form/CommentsSection"
import CustomerInteractionSection from "./review-form/CustomerInteractionSection"
import DeliveryProtocolSection from "./review-form/DeliveryProtocolSection"
import DeliverySuccessSection from "./review-form/DeliverySuccessSection"
import FormActions from "./review-form/FormActions"
import FormHeader from "./review-form/FormHeader"
import LoadCheckSection from "./review-form/LoadCheckSection"
import RoadSafetySection from "./review-form/RoadSafetySection"
import TimeManagementSection from "./review-form/TimeManagementSection"
// Import refactored components
import TrialBlockInfo from "./review-form/TrialBlockInfo"
import useReviewForm from "./review-form/useReviewForm"

interface TrialBlockReviewFormProps {
  shift: TrialBlock
  onCancel: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (formData: any) => void
}

const TrialBlockReviewForm: React.FC<TrialBlockReviewFormProps> = ({
  shift,
  onCancel,
  onSubmit,
}) => {
  const { form, calculateResult, handleFormSubmit } = useReviewForm(
    (data) => onSubmit(data),
    shift, // Pass the trial block data to initialize the form
  )

  // Handle click outside to close the dialog
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop, not the card itself
    if (e.target === e.currentTarget) {
      onCancel()
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          <FormHeader
            trialId={shift.trialId}
            trialResult={shift.result || form.watch("trialShiftResult")}
          />

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
              {/* Driver Info Section */}
              <TrialBlockInfo shift={shift} />

              {/* Attended Toggle */}
              <AttendedToggleSection control={form.control} />
              {/* Delivery Protocol Section */}
              <DeliveryProtocolSection control={form.control} calculateResult={calculateResult} />

              {/* Delivery Success Rate Section */}
              <DeliverySuccessSection control={form.control} calculateResult={calculateResult} />

              {/* Road Safety Section */}
              <RoadSafetySection control={form.control} calculateResult={calculateResult} />

              {/* Customer Interaction Section */}
              <CustomerInteractionSection
                control={form.control}
                calculateResult={calculateResult}
              />

              {/* Time Management Section */}
              <TimeManagementSection control={form.control} calculateResult={calculateResult} />

              {/* Load Check Section */}
              <LoadCheckSection control={form.control} calculateResult={calculateResult} />

              {/* Additional Trial Required */}
              <AdditionalTrialSection control={form.control} />

              {/* Comments */}
              <CommentsSection control={form.control} />

              {/* Form Actions */}
              {shift.reviewStatus === "pending" && <FormActions onCancel={onCancel} />}
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}

export default TrialBlockReviewForm
